import {useState, useEffect} from 'react';
import {Field, useFormikContext} from 'formik';

import {
    FormikNumberInput,
    FormikSwitch,
    FormikTextInput,
    PlatformFieldset
} from '@flexinet/ui-components';
import {Switch, Text} from '@mantine/core';
import {fetchAuthSession} from 'aws-amplify/auth';
import {ApplicationScreenshot} from '../types.ts';
import {AuthenticationType} from '../types.ts';


export const FormComponent = () => {

    const [useLocalSession, setUseLocalSession] = useState(true);
    const {setFieldValue} = useFormikContext<ApplicationScreenshot>();

    useEffect(() => {
        if (useLocalSession) {
            fetchAuthSession().then(({tokens}) => {
                if (tokens?.idToken && tokens?.accessToken) {
                    setFieldValue('authentication', {
                        type: AuthenticationType.COGNITO_IMPLICIT_FLOW,
                        idToken: tokens.idToken.toString(),
                        accessToken: tokens.accessToken.toString(),
                    });
                }
            });
        } else {
            setFieldValue('authentication', {
                type: AuthenticationType.COGNITO_IMPLICIT_FLOW,
                idToken: '',
                accessToken: '',
            });
        }
    }, [useLocalSession]);

    return (
        <>
            <PlatformFieldset legend={'Target'}>
                <Field
                    name='url'
                    label='URL'
                    description='Base URL of the target site (e.g. https://example.com)'
                    component={FormikTextInput}
                    withAsterisk
                    my={'xl'}
                />

                <Field
                    name='path'
                    label='Path'
                    description='Path appended to the URL (e.g. /dashboard)'
                    component={FormikTextInput}
                    my={'xl'}
                />

                <Field
                    name='selector'
                    label='CSS Selector'
                    description='Target a specific element on the page (e.g. #main-content)'
                    component={FormikTextInput}
                    my={'xl'}
                />

                <Field
                    name='waitForSelector'
                    label='Wait For Selector'
                    description='CSS selector to wait for before capture'
                    component={FormikTextInput}
                    my={'xl'}
                />
            </PlatformFieldset>

            <PlatformFieldset legend={'Viewport'}>
                <Field
                    name='viewportWidth'
                    label='Viewport Width'
                    description='Width in pixels (default: 1920)'
                    component={FormikNumberInput}
                    my={'xl'}
                />

                <Field
                    name='viewportHeight'
                    label='Viewport Height'
                    description='Height in pixels (default: 1080)'
                    component={FormikNumberInput}
                    my={'xl'}
                />

                <Field
                    name='deviceScaleFactor'
                    label='Device Scale Factor'
                    description='For retina captures, use 2 for 2x resolution (default: 1)'
                    component={FormikNumberInput}
                    my={'xl'}
                />
            </PlatformFieldset>

            <PlatformFieldset legend={'Options'}>
                <Field
                    name='fullPage'
                    label='Full Page'
                    description='Capture the entire scrollable page'
                    component={FormikSwitch}
                    my={'xl'}
                />

                <Field
                    name='hideScrollbars'
                    label='Hide Scrollbars'
                    description='Hide scrollbars in the screenshot'
                    component={FormikSwitch}
                    my={'xl'}
                />

                <Field
                    name='delay'
                    label='Delay (ms)'
                    description='Extra wait time after page load in milliseconds'
                    component={FormikNumberInput}
                    my={'xl'}
                />
            </PlatformFieldset>

            <PlatformFieldset legend={'Authentication'}>
                <Switch
                    label='Use current session'
                    description='Use your local Cognito session tokens for authenticated screenshots'
                    checked={useLocalSession}
                    onChange={(event) => setUseLocalSession(event.currentTarget.checked)}
                    my={'xl'}
                />

                {useLocalSession ? (
                    <Text size='sm' c='dimmed'>Tokens will be sourced from your current session automatically.</Text>
                ) : (
                    <>
                        <Field
                            name='authentication.idToken'
                            label='ID Token'
                            description='Cognito ID token (JWT)'
                            component={FormikTextInput}
                            my={'xl'}
                        />

                        <Field
                            name='authentication.accessToken'
                            label='Access Token'
                            description='Cognito access token (JWT)'
                            component={FormikTextInput}
                            my={'xl'}
                        />
                    </>
                )}
            </PlatformFieldset>
        </>
    );
};
